// Gob — the game-object base (extends StateMachine). game/GameObjects.cpp:188-369 +
// game/ht.h:6032-6135. Render-only members (m_trcBoundingLast, Invalidate, GetClippingBounds,
// GetUIBounds, GetSortKey consumers, draw) are EXCLUDED per scope.json; MarkRedraw ports
// because its m_ff bits are persisted state (hash-masked but saved).

import { quad } from '../hash/quads.ts';
import { StateFrame } from '../hash/stateframe.ts';
import { StateMachine } from '../sm/statemachine.ts';
import type { SimContext } from '../context.ts';
import { TcFromWc } from '../math/coords.ts';
import { s16 } from '../math/int32.ts';
import { kgidNull } from '../sm/message.ts';
import { gmpgtQuad } from './gobtypes.ts';
import type { TPoint, TRectLike, WPoint } from '../types.ts';

// game/ht.h:5984-6010 — gob flags (dword m_ff)
export const kfGobNoEnemiesNearby = 0x00000001;
export const kfGobRedraw = 0x00000002;
export const kfGobBeingUpgraded = 0x00000008;
export const kfGobBeingBuilt = 0x00000010;
export const kfGobFlashing = 0x00000040;
export const kfGobDrawFlashed = 0x00000080;
export const kfGobActive = 0x00000200;
export const kfGobStructure = 0x00000400;
export const kfGobMobileUnit = 0x00000800;
export const kfGobUnit = 0x00001000;
export const kfGobDebug = 0x00002000;
export const kfGobSelected = 0x00004000;
export const kfGobStateMachine = 0x00008000;
export const kfGobVisibleLastFrame = 0x00010000;
export const kfGobIncludeFindVisible = 0x00020000;
export const kfGobTransitioningToVisible = 0x00040000;
export const kfGobAnimationChanged = 0x00080000;
export const kfGobLayerSurfaceDecal = 0x00100000;
export const kfGobLayerDepthSorted = 0x00200000;
export const kfGobLayerSmokeFire = 0x00400000;
export const kfGobLayerSymbols = 0x00800000;
export const kfGobLayerSelection = 0x01000000;
export const kfGobLayerMask =
  kfGobLayerSurfaceDecal |
  kfGobLayerDepthSorted |
  kfGobLayerSmokeFire |
  kfGobLayerSymbols |
  kfGobLayerSelection;

// The Animation timing surface Gob's anim helpers touch (the full Animation class —
// game/Animation.cpp — is the sim-coupled timing core; ported separately).
export interface AnimationLike {
  Advance(cAdvance: number): boolean;
  GetRemainingFrameTime(): number;
  Start(nStrip: number, nFrame: number, wfAni: number): boolean;
  GetStrip(): number;
  GetFrame(): number;
  SetStrip(nStrip: number): void;
  SetFrame(nFrame: number): void;
}

/** The owner the gob layer needs (the full Player ports with game/Player.cpp). */
export interface PlayerLike {
  GetSide(): number;
  // game/Player.cpp / ht.h — the Player accessors the gob/trigger code reaches through an
  // owner reference. Signatures mirror the real Player class (the only implementer); the
  // interface was just under-specified. Type-only.
  GetId(): number;
  GetFlags(): number;
  SetFlags(wf: number): void;
  GetHandicap(): number;
  GetAllies(): number;
  IncMobileUnitsLost(): void;
  AddPowerSupplyAndDemand(nPowerSupply: number, nPowerDemand: number): void;
}

export abstract class Gob extends StateMachine {
  // public (was protected): module-level helper functions in specials.ts (shared infantry
  // bodies) and buildui.ts read gob.ctx from OUTSIDE the class hierarchy. Visibility is a
  // compile-time-only annotation (erased at runtime), so widening it changes no behavior.
  ctx: SimContext;

  m_pgobNext: Gob | null = null; // intrusive master-list link (prepend-on-add)
  m_gidNext = kgidNull; // gidMap tile-chain link
  m_gid = kgidNull;
  /** the level-file gob name (trigger/unit-group references); C++ keeps it debug-only */
  gobName: string | null = null;
  m_pplr: PlayerLike; // ctor: &gplrDummy — set a real owner before use
  m_ff = kfGobRedraw; // game/GameObjects.cpp:194
  m_wx = 0; // WCoord
  m_wy = 0;

  // game/GameObjects.cpp:188-199 (m_trcBoundingLast init omitted — render-only field)
  constructor(ctx: SimContext) {
    super();
    this.ctx = ctx;
    this.m_pplr = ctx.plrDummy;
  }

  abstract GetType(): number;

  // ht.h:6104-6131 inlines
  GetFlags(): number {
    return this.m_ff >>> 0;
  }
  SetFlags(ff: number): void {
    this.m_ff = ff >>> 0;
  }
  GetId(): number {
    return this.m_gid;
  }
  GetSide(): number {
    return this.m_pplr.GetSide();
  }
  GetOwner(): PlayerLike {
    return this.m_pplr;
  }
  SetOwner(pplr: PlayerLike): void {
    this.m_pplr = pplr;
  }

  // TS-only, client-local presentation: when true, the targeted-gob flash renders RED
  // (attack-move) instead of the usual white. The flash bits are already non-serialized /
  // non-hashed (cleared in SaveState) and set only for the local player, so this rides along
  // without any determinism or save-format impact.
  m_fAttackMoveFlash = false;

  Flash(): void {
    // ht.h:6108-6111
    this.m_ff = (this.m_ff | kfGobFlashing | kfGobRedraw) >>> 0;
    this.m_fAttackMoveFlash = false; // a normal target flash is white
    this.m_unvl.MinSkip();
  }

  /** Like Flash(), but the flash renders RED to mark an attack-move target (TS-only). */
  FlashAttackMove(): void {
    this.m_ff = (this.m_ff | kfGobFlashing | kfGobRedraw) >>> 0;
    this.m_fAttackMoveFlash = true;
    this.m_unvl.MinSkip();
  }

  // game/GameObjects.cpp:560-563
  IsSavable(): boolean {
    return true;
  }

  // game/GameObjects.cpp:525-541 (knVerGobState 2; sim-quirks B1/B2/B8) — restores the base fields and
  // re-registers at the SAVED gid (AddGob carves it from the free list)
  LoadState(pstm: import('../sm/stream.ts').Stream): boolean {
    const nVer = pstm.ReadByte();
    if (nVer !== 2) return false;
    this.m_st = pstm.ReadWord();
    // snapshotComplete (issue #96 B2): assign the saved flags. The C++/faithful path re-ORs
    // them over a freshly-constructed gob, resurrecting ctor-default bits (e.g.
    // kfGobLayerDepthSorted) that the live game had XOR-cleared — so a loaded corpse popped
    // back into the depth-sorted layer. The full dword is serialized, so assignment is exact.
    const ffSaved = pstm.ReadDword() >>> 0;
    this.m_ff = this.ctx.snapshotComplete ? ffSaved : (this.m_ff | ffSaved) >>> 0;
    this.m_wx = (pstm.ReadWord() << 16) >> 16;
    this.m_wy = (pstm.ReadWord() << 16) >> 16;
    // issue #96 (B22 + neutral): the complete snapshot encodes the owner by m_aplr INDEX
    // (0xffff = dummy) so the dummy AND the neutral player — BOTH pid 0 — round-trip exactly;
    // the faithful path reads the pid and binds via GetPlayerFromPid (pid 0 -> real m_aplr[0],
    // the shipping C++ quirk). See PlayerMgr.PlayerFromSnapshotOwnerCode.
    const ownerCode = pstm.ReadWord();
    if (this.ctx.snapshotComplete) {
      this.m_pplr = this.ctx.plrm.PlayerFromSnapshotOwnerCode(ownerCode);
    } else {
      const pplr = this.ctx.plrm.GetPlayerFromPid(ownerCode);
      this.m_pplr = pplr !== null ? pplr : this.ctx.plrDummy;
    }
    const gid = pstm.ReadWord();
    // issue #96: the state-machine "next state" + force-change flag are HASHED (STNX) but the
    // C++ format omits them — a loaded gob defaults to kstReservedGlobal and diverges once that
    // pending transition matters (e.g. neutral mission structures with m_stNext != m_st).
    if (this.ctx.snapshotComplete) {
      this.m_stNext = pstm.ReadWord();
      this.m_fForceStateChange = pstm.ReadByte() !== 0;
    }
    this.ctx.gobm.AddGob(this, gid);
    return pstm.IsSuccess();
  }

  // game/GameObjects.cpp:543-558
  SaveState(pstm: import('../sm/stream.ts').Stream): boolean {
    pstm.WriteByte(2);
    pstm.WriteWord(this.m_st);
    pstm.WriteDword(this.m_ff);
    pstm.WriteWord(this.m_wx);
    pstm.WriteWord(this.m_wy);
    // FAITHFUL: the C++ writes m_pplr->GetId() whenever m_pplr != NULL — and after the ctor it
    // never is (gplrDummy at worst, pid 0). A dummy- or neutral-owned gob therefore saves pid 0
    // and RELOADS owned by the real slot-0 player: a faithful C++ quirk (B22).
    // snapshotComplete: encode the owner by m_aplr index (0xffff = dummy) so the dummy AND the
    // neutral player (both pid 0) round-trip exactly. See PlayerMgr.SnapshotOwnerCode.
    pstm.WriteWord(
      this.ctx.snapshotComplete
        ? this.ctx.plrm.SnapshotOwnerCode(this.m_pplr)
        : (this.m_pplr as { GetId(): number }).GetId(),
    );
    pstm.WriteWord(this.m_gid);
    if (this.ctx.snapshotComplete) {
      // issue #96: hashed-but-unsaved state-machine fields
      pstm.WriteWord(this.m_stNext);
      pstm.WriteByte(this.m_fForceStateChange ? 1 : 0);
    }
    return pstm.IsSuccess();
  }

  // game/GameObjects.cpp:326-330 — redraw bookkeeping; the bits are persisted state
  MarkRedraw(): void {
    this.m_ff = (this.m_ff | kfGobRedraw) >>> 0;
    if (this.m_ff & kfGobVisibleLastFrame) {
      this.m_ff = (this.m_ff | kfGobIncludeFindVisible) >>> 0;
    }
  }

  // game/GameObjects.cpp:332-341 — STATE: drives MinSkip scheduling + anim completion
  AdvanceAnimation(pani: AnimationLike): boolean {
    if (pani.Advance(this.m_ff & kfGobAnimationChanged ? 1 : this.m_unvl.GetUpdateCount())) {
      this.m_unvl.MinSkip(pani.GetRemainingFrameTime());
      this.MarkRedraw();
      this.m_ff = (this.m_ff & ~kfGobAnimationChanged) >>> 0;
      return true;
    }
    this.m_ff = (this.m_ff & ~kfGobAnimationChanged) >>> 0;
    return false;
  }

  // game/GameObjects.cpp:343-350
  StartAnimation(pani: AnimationLike, nStrip: number, nFrame: number, wfAni: number): void {
    if (nStrip !== pani.GetStrip() || nFrame !== pani.GetFrame()) this.MarkRedraw();
    if (pani.Start(nStrip, nFrame, wfAni)) {
      this.m_unvl.MinSkip(pani.GetRemainingFrameTime());
      this.m_ff = (this.m_ff | kfGobAnimationChanged) >>> 0;
    }
  }

  // game/GameObjects.cpp:352-357
  SetAnimationStrip(pani: AnimationLike, nStrip: number): void {
    if (nStrip !== pani.GetStrip()) this.MarkRedraw();
    pani.SetStrip(nStrip);
    this.m_ff = (this.m_ff | kfGobAnimationChanged) >>> 0;
  }

  // game/GameObjects.cpp:359-364
  SetAnimationFrame(pani: AnimationLike, nFrame: number): void {
    if (nFrame !== pani.GetFrame()) this.MarkRedraw();
    pani.SetFrame(nFrame);
    this.m_ff = (this.m_ff | kfGobAnimationChanged) >>> 0;
  }

  // game/GameObjects.cpp:399-417
  GetPosition(pwpt: WPoint): void {
    pwpt.wx = this.m_wx;
    pwpt.wy = this.m_wy;
  }
  SetPosition(wx: number, wy: number): void {
    this.ctx.gobm.MoveGob(this, this.m_wx, this.m_wy, wx, wy);
    this.m_wx = s16(wx);
    this.m_wy = s16(wy);
    this.MarkRedraw();
  }
  GetCenter(pwpt: WPoint): void {
    pwpt.wx = this.m_wx;
    pwpt.wy = this.m_wy;
  }

  // game/GameObjects.cpp:419-431
  GetTilePosition(ptpt: TPoint): void {
    ptpt.tx = TcFromWc(this.m_wx);
    ptpt.ty = TcFromWc(this.m_wy);
  }
  GetTileRect(ptrc: TRectLike): void {
    ptrc.left = TcFromWc(this.m_wx);
    ptrc.top = TcFromWc(this.m_wy);
    ptrc.right = ptrc.left + 1;
    ptrc.bottom = ptrc.top + 1;
  }

  // game/GameObjects.cpp:249-264 — the per-gob hash contract. The FF mask strips the
  // machine-local/render bits; everything else is cross-machine state.
  TrackState(frame: StateFrame): number {
    const i = frame.AddCountedValue(gmpgtQuad[this.GetType()]);
    frame.AddValue(quad('ID  '), this.m_gid, i);
    frame.AddValue(quad('WX  '), this.m_wx, i);
    frame.AddValue(quad('WY  '), this.m_wy, i);
    frame.AddValue(quad('GNXT'), this.m_gidNext, i);
    frame.AddValue(quad('ST  '), this.m_st, i);
    frame.AddValue(quad('STNX'), this.m_stNext, i);
    let ff = this.m_ff >>> 0;
    ff =
      (ff &
        ~(
          kfGobRedraw |
          kfGobFlashing |
          kfGobDrawFlashed |
          kfGobDebug |
          kfGobSelected |
          kfGobVisibleLastFrame |
          kfGobIncludeFindVisible |
          kfGobTransitioningToVisible |
          kfGobAnimationChanged |
          kfGobLayerSelection |
          kfGobLayerMask
        )) >>>
      0;
    frame.AddValue(quad('FF  '), ff, i);
    return i;
  }
}
